/**
 * ActivityFormFlow
 *
 * Pure multi-step form for creating / editing emission activities. Owns form
 * state, validation, step transitions, and mutations. Does NOT own container
 * chrome — no Dashboard wrapper, no page header, no direct navigation. The
 * caller (page or modal) supplies `initiativeId`/`activityId`/`initialMethod`
 * and an `onExit` callback that fires after a successful submit (Result step
 * Done, bulk-complete CTAs) or when the user cancels.
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { generateUrl } from '@routes/util';
import { emissionsTrackerRoutes, rootAppPath } from '../../routes';
import { ArrowLeft, ArrowRight, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { BlockingLoader } from '@g17eco/atoms/loader';
import { isFugitiveGas } from '@g17eco/emissions-config';
import { getErrorMessage } from '@utils/error';
import { generateToast } from '@g17eco/molecules/toasts';
import {
  useCreateActivityMutation,
  useUpdateActivityMutation,
  useGetActivityQuery,
  useGetSettingsQuery,
  useCreateBulkImportMutation,
  useLazyGetActivityImportTemplateQuery,
  useLazyGetActivitySeedTemplateQuery,
  useCreateBulkAssetImportMutation,
} from '../../api/emissions';
import { useAppSelector } from '../../../../reducers';
import { isStaff as isStaffSelector } from '../../../../selectors/user';
import { downloadBlob } from '@utils/downloadBlob';
import { parseImportFile, type ParseResult, toActivityRequests } from '../../utils/bulkImportParser';
import { BulkImportPreview, ErrorSummary } from '../BulkImportPreview';
import {
  EmissionScope,
  Scope1ActivityType,
  ActivityType,
  FuelType,
  VehicleType,
  FugitiveGasType,
  RefrigerantGasType,
  ProcessType,
  ActivityUnit,
  CreateActivityRequest,
  getStationaryFuels,
  getMobileFuels,
  getValidUnits,
  type ActivityTypeWithUnits,
  BULK_IMPORT_MAX_ROWS,
  type ActivityWithCalculation,
  type EmissionEvidence,
  type BulkImport,
  type AssetType,
} from '../../types/emissions';
import { useEmissionsFormatter } from '../../hooks/useEmissionsFormatter';
import { expandMonthToPeriodRange } from '../../utils/activityUtils';
import { ActivityResultStep } from '../ActivityResultStep';
import {
  type FormStep,
  DataMethod,
  type FormData,
  INITIAL_FORM_DATA,
  UNIT_LABELS,
  FUEL_TYPE_LABELS,
  VEHICLE_TYPE_LABELS,
  GAS_TYPE_LABELS,
  PROCESS_TYPE_LABELS,
  SCOPE1_ACTIVITY_TYPES,
  SCOPE2_ACTIVITY_TYPES,
  getSteps,
  METHOD_CARDS,
  REQUEST_ACCESS_STEPS,
  REQUEST_METHOD_TO_STEP,
  type RequestMethod,
  isActivityFormComplete,
  type StagedRow,
  type BulkGroup,
  type DataIntent,
  type StagedAsset,
  createEmptyStagedAsset,
  updateStagedAssetMeta,
} from './types';
import { MethodStep } from './MethodStep';
import { StubEntryStep } from './multi-add/StubEntryStep';
import { ActivityEntryStep } from './multi-add/ActivityEntryStep';
import { MultiActivityReviewStep } from './multi-add/MultiActivityReviewStep';
import { MultiAddEvidenceStep } from './multi-add/MultiAddEvidenceStep';
import { MultiAddResultStep } from './multi-add/MultiAddResultStep';
import { MultiAddAssetResultStep } from './multi-add/MultiAddAssetResultStep';
import { RequestAccessStep } from './RequestAccessStep';
import { ActivityTypeStep } from './ActivityTypeStep';
import { DetailsStep } from './DetailsStep';
import { ReviewStep } from './ReviewStep';
import { BulkUploadStep } from './BulkUploadStep';
import { BulkCompleteStep } from './BulkCompleteStep';
import { AssetSelectStep } from './AssetSelectStep';
import { AssetMethodStep } from './AssetMethodStep';
import { AssetBulkUploadStep } from './AssetBulkUploadStep';
import { AssetDetailsStep } from './AssetDetailsStep';
import { AssetActivitiesStep } from './AssetActivitiesStep';
import { EvidenceStep } from './EvidenceStep';
import { useUploadSession } from '../../hooks/useUploadSession';
import { handleRouteError } from '../../../../logger';
import { getAssetTypeBrand } from '../../config/assetTypeBranding';
import '../../pages/EmissionsActivityFormPage.scss';

const SCOPE1_VALUES = new Set<string>(Object.values(Scope1ActivityType));
const toOptions = <T extends string>(values: T[], labels: Record<string, string>) =>
  values.map((v) => ({ value: v, label: labels[v] ?? v }));

const getUnitOptions = (activityType: ActivityType | '', fuelType: FuelType | '') => {
  if (!activityType) return [];
  const units = getValidUnits(activityType as ActivityTypeWithUnits, fuelType as FuelType || undefined);
  return toOptions(units, UNIT_LABELS);
};

const REQUEST_METHODS = new Set<DataMethod>(Object.keys(REQUEST_METHOD_TO_STEP) as RequestMethod[]);

const isRequestMethod = (method: DataMethod | null): method is RequestMethod =>
  method !== null && REQUEST_METHODS.has(method);

/**
 * Steps whose render depends on in-memory state that a refresh would wipe
 * out (parse results, imported rows, submit result). If the URL lands here
 * on a fresh mount we can't reconstruct the view, so the sync effect falls
 * back to the method picker.
 */
const TRANSIENT_STEPS = new Set<FormStep>([
  'result',
  'preview-import',
  'import-complete',
]);

const ALL_STEPS: readonly FormStep[] = [
  'method',
  'entry',
  'stub-entry',
  'activity',
  'details',
  'review',
  'evidence',
  'result',
  'upload',
  'preview-import',
  'import-complete',
  'asset-select',
  'asset-method',
  'asset-bulk-upload',
  'asset-details',
  'asset-activities',
  'connect-utility-request',
  'connect-financial-request',
  'connect-travel-request',
  'by-spend-request',
  'bulk-spend-request',
  'employee-survey-request',
];

const isFormStep = (value: string): value is FormStep =>
  (ALL_STEPS as readonly string[]).includes(value);

const isDataIntent = (value: string): value is DataIntent =>
  value === 'activity' ||
  value === 'asset' ||
  value === 'spend' ||
  value === 'travel' ||
  value === 'logistics' ||
  value === 'survey';

const isDataMethod = (value: string): value is DataMethod =>
  value === 'log-activity' ||
  value === 'bulk-upload' ||
  value === 'from-inventory' ||
  value === 'connect-utility' ||
  value === 'connect-financial' ||
  value === 'connect-travel' ||
  value === 'scan-invoice' ||
  value === 'by-spend' ||
  value === 'bulk-spend' ||
  value === 'employee-survey';

const startStepFor = (method: DataMethod | null): FormStep => {
  switch (method) {
    case 'from-inventory':
      return 'asset-select';
    case 'log-activity':
      // GU-7008 redesign: log-activity now lands in the new multi-add
      // entry step (asset-page-style table + Add Activity modal); the
      // legacy 4-step single-activity wizard is retired for this path.
      return 'entry';
    case 'bulk-upload':
      return 'activity';
    case 'scan-invoice':
      // GU-6981: scan-invoice is an in-flow modal inside the multi-add
      // entry step, NOT a full-page wizard. Route the user into the
      // entry table with the modal auto-opened; the entry step itself
      // reads `dataMethod === ScanInvoice` to know whether to open.
      return 'entry';
    case 'connect-utility':
    case 'connect-financial':
    case 'connect-travel':
    case 'by-spend':
    case 'bulk-spend':
    case 'employee-survey':
      return REQUEST_METHOD_TO_STEP[method];
    case null:
    default:
      return 'method';
  }
};

export interface ActivityFormFlowProps {
  initiativeId: string;
  activityId?: string;
  /** Skip the Method step and jump directly to the first data step for this method. */
  initialMethod?: DataMethod;
  /**
   * Called after a successful submit (Result step Done, bulk-complete CTAs)
   * and when the user wants to cancel/close. The caller decides what this
   * means — `history.push` to an activities list, close a modal, etc.
   */
  onExit: () => void;
}

export const ActivityFormFlow = ({
  initiativeId,
  activityId,
  initialMethod,
  onExit,
}: ActivityFormFlowProps) => {
  const isEditing = Boolean(activityId);
  const history = useHistory();

  // API hooks
  const { data: existingActivity, isLoading: isLoadingActivity } = useGetActivityQuery(
    { initiativeId, activityId: activityId ?? '' },
    { skip: !activityId || !initiativeId }
  );

  const { data: settings } = useGetSettingsQuery(
    { initiativeId: initiativeId ?? '' },
    { skip: !initiativeId }
  );


  // Shared upload session so EvidenceStep (staging files) and the asset
  // flow's Review submit (promoting those files once real activity IDs
  // exist) agree on which session holds the staged uploads. Passed into
  // EvidenceStep via the `uploadSession` prop.
  const uploadSession = useUploadSession({ initiativeId: initiativeId ?? '' });

  const [createActivity, { isLoading: isCreating, error: createError }] = useCreateActivityMutation();
  const [updateActivity, { isLoading: isUpdating, error: updateError }] = useUpdateActivityMutation();
  const [createBulkImport, { isLoading: isImporting }] = useCreateBulkImportMutation();
  const [createBulkAssetImport, { isLoading: isCreatingAsset }] = useCreateBulkAssetImportMutation();
  const [fetchTemplate, { isFetching: isDownloadingTemplate }] = useLazyGetActivityImportTemplateQuery();
  const [fetchSeedTemplate, { isFetching: isDownloadingSeedTemplate }] = useLazyGetActivitySeedTemplateQuery();
  const staffUser = useAppSelector(isStaffSelector);

  // Form state — when editing, skip directly to details and treat as ByActivity.
  // `initialMethod` (from caller) lets us jump past the Method step to the first
  // data step of that method: e.g. ByAsset opens directly on asset-select.
  const startStep: FormStep = isEditing ? 'details' : startStepFor(initialMethod ?? null);
  const startMethod: DataMethod | null = isEditing ? DataMethod.ByActivity : initialMethod ?? null;
  const [currentStep, setCurrentStep] = useState<FormStep>(startStep);
  const [dataMethod, setDataMethod] = useState<DataMethod | null>(startMethod);
  // Tracks which intent was picked on Step 1 — only set when the user lands
  // in a stub-entry step (Spend / Travel / Shipment / Survey). Held both
  // in state and in the URL so a refresh restores the right view.
  const [dataIntent, setDataIntent] = useState<DataIntent | null>(null);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [submitResult, setSubmitResult] = useState<ActivityWithCalculation | null>(null);
  const [attachedEvidences, setAttachedEvidences] = useState<EmissionEvidence[]>([]);

  // GU-7008 multi-add flow state. Staged rows + bulk groups live in the
  // entry step (in-memory only). On submit success we clear these — the
  // activities now live in the DB, so Review is the source of truth and
  // navigating back to Entry shows a fresh blank table. The Cancel-batch
  // path on Review undoes the imports first, also leaving Entry empty.
  const [stagedRows, setStagedRows] = useState<StagedRow[]>([]);
  const [stagedBulkGroups, setStagedBulkGroups] = useState<BulkGroup[]>([]);
  const [submittedImportIds, setSubmittedImportIds] = useState<string[]>([]);
  // Assets created in the asset wizard's submit. Populated from each
  // BulkImport response's `assetIds`. Used by `MultiAddAssetResultStep`
  // to drive "View Asset" (single) / "View Assets" (many) CTAs.
  const [submittedAssetIds, setSubmittedAssetIds] = useState<string[]>([]);
  const formInitialized = useRef(false);

  // URL ↔ step sync. The URL carries `?step=<id>&method=<id>` and is the
  // single source of truth for the wizard's position. `navigateToStep`
  // pushes an entry (so browser back/forward traverse the wizard); the
  // effect reads `location.search` on every change and applies it to
  // state. This means forward moves and popstate-driven moves run through
  // the same path.
  const location = useLocation();

  const navigateToStep = useCallback(
    (step: FormStep, method?: DataMethod | null, options?: { replace?: boolean; intent?: DataIntent | null }) => {
      const resolvedMethod = method === undefined ? dataMethod : method;
      const resolvedIntent = options?.intent === undefined ? dataIntent : options.intent;
      const query = new URLSearchParams();
      query.set('step', step);
      if (resolvedMethod) {
        query.set('method', resolvedMethod);
      }
      if (resolvedIntent) {
        query.set('intent', resolvedIntent);
      }
      const url = `${location.pathname}?${query.toString()}`;
      if (options?.replace) {
        history.replace(url);
      } else {
        history.push(url);
      }
    },
    [dataMethod, dataIntent, history, location.pathname],
  );

  useEffect(() => {
    // Edit mode opens directly on `details` with the activityId in the path;
    // the URL sync is bypassed so we don't rewrite `/activity/:id` with a
    // query string on mount. Everything else flows through the query.
    if (isEditing) {
      return;
    }
    const params = new URLSearchParams(location.search);
    const rawStep = params.get('step');
    const rawMethod = params.get('method');
    const rawIntent = params.get('intent');

    // No step in URL (first mount with a clean path) — write the start step
    // so browser back has something to leave from, using `replace` to avoid
    // adding an extra history entry.
    if (!rawStep) {
      const initial = startStepFor(initialMethod ?? null);
      const query = new URLSearchParams();
      query.set('step', initial);
      if (initialMethod) {
        query.set('method', initialMethod);
      }
      history.replace(`${location.pathname}?${query.toString()}`);
      return;
    }

    // Unknown step id — fall back to the method picker.
    if (!isFormStep(rawStep)) {
      history.replace(`${location.pathname}?step=method`);
      return;
    }

    // Transient steps can't be reconstructed on refresh.
    if (TRANSIENT_STEPS.has(rawStep)) {
      history.replace(`${location.pathname}?step=method`);
      return;
    }

    const nextMethod: DataMethod | null =
      rawMethod && isDataMethod(rawMethod) ? rawMethod : null;
    const nextIntent: DataIntent | null =
      rawIntent && isDataIntent(rawIntent) ? rawIntent : null;

    // Any step past `method` requires either a method (most flows) or — for
    // the intent-keyed stub entry — an intent.
    const isStubEntry = rawStep === 'stub-entry';
    if (rawStep !== 'method' && nextMethod === null && !(isStubEntry && nextIntent !== null)) {
      history.replace(`${location.pathname}?step=method`);
      return;
    }

    setCurrentStep(rawStep);
    setDataMethod(nextMethod);
    setDataIntent(nextIntent);
  }, [location.pathname, location.search, isEditing, initialMethod, history]);

  // Bulk import state
  const [importFile, setImportFile] = useState<File | undefined>();
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<BulkImport | null>(null);
  const { formatCompact } = useEmissionsFormatter();

  // Initialize form with existing data when editing — only runs once on first load.
  // Guard against re-running when RTK Query re-fetches after a successful update.
  useEffect(() => {
    if (existingActivity && isEditing && !formInitialized.current) {
      formInitialized.current = true;
      setFormData({
        ...INITIAL_FORM_DATA,
        scope: existingActivity.scope,
        activityType: existingActivity.activityType,
        periodStart: existingActivity.periodStart.slice(0, 7),
        periodEnd: existingActivity.periodEnd.slice(0, 7),
        quantity: String(existingActivity.quantity),
        unit: existingActivity.unit,
        fuelType: existingActivity.fuelType ?? '',
        vehicleType: existingActivity.vehicleType ?? '',
        refrigerantType: existingActivity.refrigerantType ?? existingActivity.gasType ?? '',
        processType: existingActivity.processType ?? '',
        region: existingActivity.region,
        regionFallback: existingActivity.regionFallback ?? true,
        yearFallback: existingActivity.yearFallback ?? true,
        dataSource: existingActivity.dataSource ?? '',
        notes: existingActivity.notes ?? '',
        name: existingActivity.name ?? '',
      });
      // Skip to details step when editing
      setCurrentStep('details');
    }
  }, [existingActivity, isEditing]);

  // Pre-fill region from organization settings when creating new activity
  useEffect(() => {
    if (!isEditing && settings?.defaultRegion && formData.region === '') {
      setFormData((prev) => ({ ...prev, region: settings.defaultRegion }));
    }
  }, [isEditing, settings?.defaultRegion, formData.region]);

  const unitOptions = useMemo(
    () => getUnitOptions(formData.activityType, formData.fuelType),
    [formData.activityType, formData.fuelType]
  );

  const fuelTypeOptions = useMemo(
    () => {
      if (formData.activityType === Scope1ActivityType.MobileCombustion) {
        const scope = formData.scope || EmissionScope.Scope1;
        return toOptions(getMobileFuels(formData.vehicleType as VehicleType || undefined, scope), FUEL_TYPE_LABELS);
      }
      return toOptions(getStationaryFuels(), FUEL_TYPE_LABELS);
    },
    [formData.activityType, formData.vehicleType, formData.scope]
  );

  const vehicleTypeOptions = useMemo(
    () => toOptions(Object.values(VehicleType), VEHICLE_TYPE_LABELS),
    []
  );

  const refrigerantTypeOptions = useMemo(
    () => toOptions([...Object.values(RefrigerantGasType), ...Object.values(FugitiveGasType)], GAS_TYPE_LABELS),
    []
  );

  const processTypeOptions = useMemo(
    () => toOptions(Object.values(ProcessType), PROCESS_TYPE_LABELS),
    []
  );

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // Auto-derive scope from activity type and reset dependent fields
      if (field === 'activityType') {
        updated.scope = SCOPE1_VALUES.has(String(value)) ? EmissionScope.Scope1 : EmissionScope.Scope2;
        updated.unit = '';
        updated.fuelType = '';
        updated.vehicleType = '';
        updated.refrigerantType = '';
        updated.processType = '';
      }

      // Reset unit if it's no longer valid for the selected fuel type
      if (field === 'fuelType' && value && prev.activityType) {
        const validUnits = getValidUnits(prev.activityType as ActivityTypeWithUnits, value as FuelType);
        if (validUnits.length > 0 && prev.unit && !validUnits.includes(prev.unit as ActivityUnit)) {
          updated.unit = '';
        }
      }

      return updated;
    });
  };

  // Single source of truth in `types.ts` — keeps the wizard's Next gate,
  // the AddActivityModal save gate, and the AssetActivitiesStep modal
  // gate aligned. Don't reintroduce per-field checks here.
  const canProceedFromDetails = isActivityFormComplete(formData);

  const steps = getSteps(dataMethod);

  const getStepIndex = (step: FormStep): number => steps.findIndex((s) => s.id === step);

  const handleNext = () => {
    const idx = getStepIndex(currentStep);
    if (idx < 0 || idx >= steps.length - 1) {
      return;
    }
    const next = steps[idx + 1].id;
    if (next.startsWith('ghost:')) {
      return;
    }
    navigateToStep(next as FormStep);
  };

  const handleBack = () => {
    // Preview-only request-access steps reset the chosen method on back
    if (REQUEST_ACCESS_STEPS.includes(currentStep)) {
      navigateToStep('method', null);
      return;
    }
    // `stub-entry` lives outside the per-method step strip — Back always
    // returns to the intent picker and clears the intent.
    if (currentStep === 'stub-entry') {
      navigateToStep('method', null, { intent: null });
      return;
    }
    // `entry` (multi-add) sits at the top of LOG_ACTIVITY_STEPS so the
    // generic "previous step" logic below would no-op. Back on entry
    // means "abandon this batch and return to the intent picker"; the
    // staged rows live in parent state and survive the navigation.
    if (currentStep === 'entry') {
      navigateToStep('method', null, { intent: null });
      return;
    }
    // `asset-method` is a hidden sub-step of `asset-select` (it picks
    // Manual / Bulk / Existing after the type is chosen). Back returns
    // to the type picker and clears the picked type so the user can
    // change it.
    if (currentStep === 'asset-method') {
      setFormData((prev) => ({ ...prev, assetType: '', stagedAssets: [] }));
      navigateToStep('asset-select');
      return;
    }
    // `asset-bulk-upload` is the dropzone view reached from
    // `asset-method`'s "Upload spreadsheet" card. Back returns to the
    // path picker so the user can switch to Manual / Existing without
    // losing their chosen type.
    if (currentStep === 'asset-bulk-upload') {
      navigateToStep('asset-method');
      return;
    }
    // `asset-details` was reached via `asset-method`'s "Add one" card.
    // Chrome Back skips the hidden sub-step back to the path picker
    // so the user can switch Manual ↔ Bulk without re-picking type.
    if (currentStep === 'asset-details') {
      navigateToStep('asset-method');
      return;
    }
    const idx = getStepIndex(currentStep);
    if (idx <= 0) {
      return;
    }
    const prev = steps[idx - 1].id;
    if (prev.startsWith('ghost:')) {
      return;
    }
    // Transient steps don't write to the URL (they live in component
    // state only — see TRANSIENT_STEPS). When backing out of one, the
    // URL is still pinned to the previous URL-routable step, so calling
    // `navigateToStep` would push the same URL and the sync effect
    // wouldn't fire. Update state directly instead.
    if (TRANSIENT_STEPS.has(currentStep)) {
      setCurrentStep(prev as FormStep);
      return;
    }
    navigateToStep(prev as FormStep);
  };

  // Bulk import handlers
  const resetImportState = () => {
    setImportFile(undefined);
    setParseResult(null);
    setParseError(null);
    setImportError(null);
    setImportResult(null);
  };

  const handleDownloadTemplate = async () => {
    if (!formData.scope) {
      return;
    }
    const isScope1 = formData.scope === EmissionScope.Scope1;
    const result = await fetchTemplate({
      initiativeId,
      scope: formData.scope as EmissionScope,
      ...(isScope1 && formData.activityType && { activityType: formData.activityType as Scope1ActivityType }),
    });
    if (result.data) {
      const suffix = formData.activityType ? `-${formData.activityType}` : '';
      downloadBlob(result.data, `emissions-${formData.scope}${suffix}-import-template.xlsx`);
      return;
    }
    const detail = result.error ? `: ${getErrorMessage(result.error)}` : '';
    setParseError(`Template download failed${detail}`);
  };

  const handleDownloadSeedTemplate = async ({ rowCount, startYear, endYear }: { rowCount: number; startYear: number; endYear: number }) => {
    if (!formData.scope) {
      return;
    }
    const isScope1 = formData.scope === EmissionScope.Scope1;
    const result = await fetchSeedTemplate({
      initiativeId,
      scope: formData.scope as EmissionScope,
      ...(isScope1 && formData.activityType && { activityType: formData.activityType as Scope1ActivityType }),
      rowCount,
      startYear,
      endYear,
    });
    if (result.data) {
      const suffix = formData.activityType ? `-${formData.activityType}` : '';
      downloadBlob(result.data, `emissions-${formData.scope}${suffix}-seed-template.xlsx`);
      return;
    }
    const detail = result.error ? `: ${getErrorMessage(result.error)}` : '';
    setParseError(`Seed template download failed${detail}`);
  };

  const handleFilesAdded = async (files: File[]) => {
    const file = files[0];
    if (!file || !formData.scope) return;
    setImportFile(file);
    setParseError(null);
    try {
      const result = await parseImportFile(file, formData.scope as EmissionScope);
      setParseResult(result);
      setCurrentStep('preview-import');
    } catch (error) {
      setParseError(error instanceof Error ? error.message : 'Failed to parse file');
      setParseResult(null);
    }
  };

  const handleFileRemove = () => {
    setImportFile(undefined);
    setParseResult(null);
    setParseError(null);
  };

  const handleImport = async (result: ParseResult) => {
    if (!formData.scope || !importFile || !initiativeId) return;
    const activities = toActivityRequests(result);
    if (activities.length === 0) return;
    if (activities.length > BULK_IMPORT_MAX_ROWS) {
      setImportError(`Too many rows: ${activities.length} valid activities exceed the maximum of ${BULK_IMPORT_MAX_ROWS} per import. Please split your file into smaller batches.`);
      return;
    }
    try {
      const importRes = await createBulkImport({
        initiativeId,
        file: importFile,
        activities: JSON.stringify(activities),
        scope: formData.scope as EmissionScope,
        regionFallback: true,
        yearFallback: true,
      }).unwrap();
      setImportResult(importRes);
      setCurrentStep('import-complete');
    } catch (err) {
      setImportError(getErrorMessage(err));
    }
  };

  /**
   * Commit the upload session then run the caller's onExit. Used from every
   * successful terminal path (submit done, edit saved, view activities).
   * Committing flags the session as done so the next flow's hydrate-on-mount
   * doesn't surface its already-promoted files with a disabled picker.
   * Bulk imports don't use the session — commitSession is a no-op there.
   */
  const handleExitAfterSubmit = async () => {
    await uploadSession.commitSession();
    onExit();
  };

  const handleSubmit = async () => {
    if (!initiativeId) {
      setSubmitError('Unable to submit: organization context is missing. Please navigate back and try again.');
      return;
    }

    setSubmitError(null);

    const { periodStart, periodEnd } = expandMonthToPeriodRange(
      formData.periodStart,
      formData.periodEnd,
    );
    const activityData: CreateActivityRequest = {
      scope: formData.scope as EmissionScope,
      activityType: formData.activityType as ActivityType,
      periodStart,
      periodEnd,
      quantity: parseFloat(formData.quantity),
      unit: formData.unit as ActivityUnit,
      region: formData.region,
      regionFallback: formData.regionFallback,
      yearFallback: formData.yearFallback,
      dataSource: formData.dataSource || undefined,
      notes: formData.notes || undefined,
      name: formData.name || undefined,
      fuelType: formData.fuelType ? (formData.fuelType as FuelType) : undefined,
      vehicleType: formData.vehicleType ? (formData.vehicleType as VehicleType) : undefined,
      refrigerantType: formData.refrigerantType && !isFugitiveGas(formData.refrigerantType)
        ? (formData.refrigerantType as RefrigerantGasType) : undefined,
      gasType: formData.refrigerantType && isFugitiveGas(formData.refrigerantType)
        ? (formData.refrigerantType as FugitiveGasType) : undefined,
      processType: formData.processType ? (formData.processType as ProcessType) : undefined,
    };

    try {
      if (isEditing && activityId) {
        await updateActivity({
          initiativeId,
          activityId,
          data: activityData,
        }).unwrap();
        // Give the user a chance to add evidence to the edited activity.
        navigateToStep('evidence');
      } else {
        const result = await createActivity({
          initiativeId,
          data: activityData,
        }).unwrap();
        setSubmitResult(result);
        // Reset so the Result step doesn't echo evidences from a prior
        // create in the same session (onAddAnother also resets, but users
        // can reach this path via Back → Review → Create Activity).
        setAttachedEvidences([]);
        navigateToStep('evidence');
      }
    } catch (error) {
      setSubmitError(getErrorMessage(error));
    }
  };

  const handleAssetSubmit = async () => {
    if (!initiativeId) {
      setSubmitError('Unable to submit: organization context is missing.');
      return;
    }
    if (!formData.assetType) {
      setSubmitError('Asset type is required before submit.');
      return;
    }
    if (formData.stagedAssets.length === 0) {
      setSubmitError('No asset to submit.');
      return;
    }
    setSubmitError(null);

    // Group by `bulkGroupId`: undefined = manual-add (one call, no
    // file), each defined id = one parsed XLSX file (one call, with
    // file). The two paths share the same wire shape so we stage them
    // identically and only fork on the file attach.
    const groups = new Map<string | null, StagedAsset[]>();
    for (const staged of formData.stagedAssets) {
      const key = staged.bulkGroupId ?? null;
      const bucket = groups.get(key);
      if (bucket) {
        bucket.push(staged);
      } else {
        groups.set(key, [staged]);
      }
    }

    const buildPayload = (assets: StagedAsset[]) => assets.map((staged) => ({
      asset: staged.asset,
      activities: staged.activities
        .filter((row) => row.request.quantity && row.request.unit)
        .map((row) => {
          const a = row.request;
          const { periodStart, periodEnd } = expandMonthToPeriodRange(a.periodStart, a.periodEnd);
          return {
            scope: a.scope,
            activityType: a.activityType,
            periodStart,
            periodEnd,
            quantity: Number(a.quantity),
            unit: a.unit as ActivityUnit,
            region: a.region,
            regionFallback: a.regionFallback,
            yearFallback: a.yearFallback,
            ...(a.fuelType ? { fuelType: a.fuelType as FuelType } : {}),
            ...(a.vehicleType ? { vehicleType: a.vehicleType as VehicleType } : {}),
            // The wizard's combined "gas" picker stores the value on
            // `refrigerantType` regardless of kind. Split it back into
            // the server's distinct `refrigerantType` / `gasType`
            // fields here so SF6 / NF3 / PFCs route to `gasType`
            // (where they actually belong) and don't fail the
            // refrigerant-only zod enum at factor-search time.
            ...(a.refrigerantType && isFugitiveGas(a.refrigerantType)
              ? { gasType: a.refrigerantType as FugitiveGasType }
              : a.refrigerantType
                ? { refrigerantType: a.refrigerantType as RefrigerantGasType }
                : {}),
            ...(a.processType ? { processType: a.processType as ProcessType } : {}),
            ...(a.name ? { name: a.name.trim() } : {}),
            ...(a.dataSource ? { dataSource: a.dataSource.trim() } : {}),
            ...(a.notes ? { notes: a.notes.trim() } : {}),
            ...(row.overrideFactorId ? { climatiqFactorId: row.overrideFactorId } : {}),
          };
        }),
    }));

    try {
      const importIds: string[] = [];
      const createdAssetIds: string[] = [];
      let totalFailedRows = 0;
      let totalRows = 0;
      const distinctErrors = new Set<string>();

      for (const [bulkGroupId, assets] of groups) {
        const payload = buildPayload(assets);
        const file = bulkGroupId
          ? stagedBulkGroups.find((g) => g.id === bulkGroupId)?.file
          : undefined;
        const result = await createBulkAssetImport({
          initiativeId,
          assets: JSON.stringify(payload),
          assetType: formData.assetType as string,
          ...(file ? { file } : {}),
        }).unwrap();
        importIds.push(result._id);
        if (result.assetIds && result.assetIds.length > 0) {
          createdAssetIds.push(...result.assetIds);
        }
        totalFailedRows += result.failedRows;
        totalRows += result.totalRows;
        for (const r of result.rowResults) {
          if (!r.success && r.error) {
            distinctErrors.add(r.error);
          }
        }
      }

      if (totalFailedRows > 0) {
        const head = [...distinctErrors].slice(0, 3).join('; ');
        const tail = distinctErrors.size > 3 ? ` (+${distinctErrors.size - 3} more)` : '';
        generateToast({
          color: 'warning',
          title: `${totalFailedRows} of ${totalRows} asset${totalRows === 1 ? '' : 's'} failed`,
          message: `${head || 'See asset list for details.'}${tail}. Retry from the asset detail page.`,
        });
      }

      // Evidence + result reuse the multi-add pattern: store the
      // returned import ids so `MultiAddEvidenceStep` can render the
      // post-submit attach view, and `result` reads the same list.
      setSubmittedImportIds(importIds);
      setSubmittedAssetIds(createdAssetIds);
      navigateToStep('evidence');
    } catch (error) {
      setSubmitError(getErrorMessage(error));
    }
  };

  // Hidden sub-steps are children of a visible parent in the strip —
  // the progress bar should park on the parent so the user sees a
  // labelled "current step" rather than every column rendering as
  // future (which is what happens when `getStepIndex` returns -1).
  //
  // `stub-entry` is the placeholder Step 2 for stubbed intents
  // (Spend / Travel / Shipment / Survey) before the user picks a
  // sub-method. It uses the default LOG_ACTIVITY_STEPS strip via
  // `getSteps(null)` (since `dataMethod` is still null) — we park
  // the strip on `method` so the user sees they progressed past
  // selection. Once they pick a method on stub-entry, the wizard
  // sets `dataMethod` and switches to that flow's own strip.
  const HIDDEN_STEP_PARENTS: Partial<Record<FormStep, FormStep>> = {
    'asset-method': 'asset-select',
    'asset-bulk-upload': 'asset-select',
    'stub-entry': 'method',
  };
  const visibleStepForStrip = HIDDEN_STEP_PARENTS[currentStep] ?? currentStep;
  const stepIndex = getStepIndex(visibleStepForStrip);
  const progress = ((stepIndex + 1) / steps.length) * 100;
  const error = submitError
    || (createError ? getErrorMessage(createError) : null)
    || (updateError ? getErrorMessage(updateError) : null);

  if (isLoadingActivity && isEditing) {
    return (
      <div className='tw:text-center tw:py-12'>
        <Loader2 className='tw:size-8 tw:animate-spin tw:text-muted-foreground tw:mx-auto tw:mb-3' />
        <p className='tw:text-sm tw:text-muted-foreground'>Loading activity...</p>
      </div>
    );
  }

  return (
    <>
      {/* Asset wizard submit can fan out into N `createBulkAssetImport`
        * calls (one per `bulkGroupId`) which take real time. Mount a
        * full-page blocker so the user can't edit staged rows or click
        * buttons while it's in flight — server is mid-write and any
        * change to the staged state would be ignored or worse.
        */}
      {isCreatingAsset && <BlockingLoader />}

      {/* Progress Bar */}
      <div className='tw:mb-6'>
        <div className='tw:flex tw:justify-between tw:mb-2'>
          {steps.map((step, index) => {
            const allActivityTypes = [...SCOPE1_ACTIVITY_TYPES, ...SCOPE2_ACTIVITY_TYPES];
            const assetTypeBrand = formData.assetType ? getAssetTypeBrand(formData.assetType) : null;
            const methodLabel = dataMethod
              ? METHOD_CARDS.find((card) => card.key === dataMethod)?.label ?? null
              : null;
            const firstStagedAssetName = formData.stagedAssets[0]?.asset.name.trim();
            const sublabel =
              step.id === 'method' && methodLabel
                ? methodLabel
                : step.id === 'activity' && formData.activityType
                  ? (allActivityTypes.find((t) => t.value === formData.activityType)?.label ?? '')
                  : step.id === 'activity' && formData.scope === EmissionScope.Scope2 && dataMethod === DataMethod.BulkUpload
                    ? 'Purchased Energy'
                    : step.id === 'asset-select' && assetTypeBrand
                      ? assetTypeBrand.label
                      : step.id === 'asset-details' && firstStagedAssetName
                        ? firstStagedAssetName
                        : null;

            return (
              <div
                key={step.id}
                className={cn(
                  index < stepIndex
                    ? 'tw:cursor-pointer tw:hover:opacity-80'
                    : ''
                )}
                onClick={index < stepIndex && !step.id.startsWith('ghost:') ? () => navigateToStep(step.id as FormStep) : undefined}
              >
                <div className={cn(
                  'tw:text-sm',
                  index <= stepIndex ? 'tw:text-emerald-700 tw:font-medium' : 'tw:text-muted-foreground'
                )}>
                  {step.label}
                </div>
                {sublabel && index <= stepIndex && (
                  <div className='tw:text-xs tw:text-emerald-600 tw:truncate tw:max-w-[200px]'>
                    {sublabel}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className='tw:h-1.5 tw:w-full tw:rounded-full tw:bg-muted tw:overflow-hidden'>
          <div
            className='tw:h-full tw:rounded-full tw:bg-emerald-600 tw:transition-all'
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {error && (
        <div className='tw:flex tw:gap-2 tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-3 tw:text-red-800 tw:text-sm tw:mb-4'>
          <AlertCircle className='tw:size-4 tw:shrink-0 tw:mt-0.5' />
          {error}
        </div>
      )}

      {/* Step 1: Method Selection */}
      {currentStep === 'method' && (
        <MethodStep
          onPickIntent={(intent) => {
            // Auto-advance into the intent-specific step 2.
            // - activity: existing multi-add Entry (functional)
            // - asset:    existing asset wizard's first step (functional)
            // - all others: shared StubEntryStep keyed by intent.
            if (intent === 'activity') {
              navigateToStep('entry', DataMethod.ByActivity, { intent });
              return;
            }
            if (intent === 'asset') {
              navigateToStep('asset-select', DataMethod.ByAsset, { intent });
              return;
            }
            navigateToStep('stub-entry', null, { intent });
          }}
        />
      )}

      {/* Step 2 for stubbed intents (Spend / Travel / Shipment / Survey).
        * Back is handled by the wizard footer — see handleBack's
        * stub-entry branch which clears the intent on its way back to
        * the picker. */}
      {currentStep === 'stub-entry' && dataIntent && (
        <StubEntryStep
          intent={dataIntent}
          onSelectMethod={(method) => navigateToStep(startStepFor(method), method)}
          onNavigateExternal={(route) => {
            if (route === 'manage-assets') {
              history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ASSETS, { rootAppPath, initiativeId }));
            }
          }}
        />
      )}

      {/* Request Access mockup step for preview-only flows */}
      {REQUEST_ACCESS_STEPS.includes(currentStep) && isRequestMethod(dataMethod) && (
        <RequestAccessStep method={dataMethod} />
      )}

      {/* GU-7008 + GU-6981: log-activity Step 2 — multi-add activities entry
        * table. Also serves as the host for the Scan Invoice modal: when
        * the user picks Scan Invoice from outside the entry step (intent
        * picker / stub-entry / method tile), `startStepFor('scan-invoice')`
        * routes here with `dataMethod === ScanInvoice`, and the entry step
        * auto-opens the modal. From inside the entry step the user can
        * also open the modal via the toolbar's Scan Invoice button. */}
      {currentStep === 'entry'
        && (dataMethod === DataMethod.ByActivity || dataMethod === DataMethod.ScanInvoice)
        && (
        <ActivityEntryStep
          initiativeId={initiativeId}
          rows={stagedRows}
          setRows={setStagedRows}
          bulkGroups={stagedBulkGroups}
          setBulkGroups={setStagedBulkGroups}
          defaultRegion={formData.region || settings?.defaultRegion || ''}
          uploadSession={uploadSession}
          autoOpenScanInvoiceModal={dataMethod === DataMethod.ScanInvoice}
          onSubmitSuccess={(importIds) => {
            setSubmittedImportIds(importIds);
            // Activities are now persisted — drop staged state so going
            // back to Entry doesn't show phantom rows that would re-create
            // duplicates on a second Submit.
            setStagedRows([]);
            setStagedBulkGroups([]);
            navigateToStep('review');
          }}
        />
      )}

      {/* Step 2: Activity Type Selection */}
      {currentStep === 'activity' && (
        <ActivityTypeStep
          dataMethod={dataMethod}
          formData={formData}
          onSelect={(activityType) => { updateField('activityType', activityType as ActivityType); handleNext(); }}
          onSelectScope2Bulk={() => {
            setFormData((prev) => ({ ...prev, scope: EmissionScope.Scope2, activityType: '' }));
            handleNext();
          }}
        />
      )}

      {/* Step 3: Activity Details */}
      {currentStep === 'details' && (
        <DetailsStep
          formData={formData}
          updateField={updateField}
        />
      )}

      {/* Step 4: Review (ByActivity + ScanInvoice both use the multi-add
          review; ByAsset has no review step — submit fires from
          `asset-activities`). */}
      {currentStep === 'review'
        && (dataMethod === DataMethod.ByActivity || dataMethod === DataMethod.ScanInvoice)
        && (
        <MultiActivityReviewStep
          initiativeId={initiativeId}
          importIds={submittedImportIds}
          onCancel={() => {
            // Undo already happened inside MultiActivityReviewStep — the
            // activities are gone from the DB. Land back on Entry with a
            // clean slate (staged rows were cleared on submit success).
            setSubmittedImportIds([]);
            navigateToStep('entry');
          }}
          onContinue={() => navigateToStep('evidence')}
        />
      )}
      {currentStep === 'review'
        && dataMethod !== DataMethod.ByAsset
        && dataMethod !== DataMethod.ByActivity
        && dataMethod !== DataMethod.ScanInvoice
        && (
        <ReviewStep
          formData={formData}
          unitOptions={unitOptions}
          vehicleTypeOptions={vehicleTypeOptions}
          fuelTypeOptions={fuelTypeOptions}
          refrigerantTypeOptions={refrigerantTypeOptions}
          processTypeOptions={processTypeOptions}
        />
      )}

      {/* Bulk: Upload File */}
      {currentStep === 'upload' && (
        <BulkUploadStep
          initiativeId={initiativeId}
          formData={formData}
          importFile={importFile}
          parseError={parseError}
          isDownloadingTemplate={isDownloadingTemplate}
          onDownloadTemplate={handleDownloadTemplate}
          onFilesAdded={handleFilesAdded}
          onFileRemove={handleFileRemove}
          onBack={handleBack}
          isStaff={staffUser}
          onDownloadSeedTemplate={handleDownloadSeedTemplate}
          isDownloadingSeedTemplate={isDownloadingSeedTemplate}
        />
      )}

      {/* Bulk: Preview */}
      {currentStep === 'preview-import' && parseResult && (
        <>
          {isImporting && <BlockingLoader />}
          {importError && (
            <div className='tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-4 tw:mb-4 tw:flex tw:items-center tw:gap-2 tw:text-red-800 tw:text-sm'>
              <AlertCircle className='tw:size-4 tw:shrink-0' />
              {importError}
            </div>
          )}
          <ErrorSummary parseResult={parseResult} />
          <BulkImportPreview
            parseResult={parseResult}
            onConfirm={() => handleImport(parseResult)}
            onCancel={() => { resetImportState(); navigateToStep('upload'); }}
            isImporting={isImporting}
          />
        </>
      )}

      {/* Bulk: Complete */}
      {currentStep === 'import-complete' && importResult && (
        <BulkCompleteStep
          importResult={importResult}
          onImportMore={() => {
            resetImportState();
            setFormData({ ...INITIAL_FORM_DATA });
            navigateToStep('method', null);
          }}
          onViewActivities={handleExitAfterSubmit}
          onViewFailedActivities={handleExitAfterSubmit}
        />
      )}

      {/* Asset Wizard: Select Asset (stage 1 — type picker) */}
      {currentStep === 'asset-select' && (
        <AssetSelectStep
          initiativeId={initiativeId}
          onPickType={(assetType: AssetType) => {
            // Stash the type on formData so the hidden `asset-method`
            // sub-step can read it. The empty stagedAssets shell is
            // seeded later — we only commit a slot once the user picks
            // Manual on the next screen, so swapping back to Bulk
            // doesn't leave a stray manual asset around.
            setFormData((prev) => ({ ...prev, assetType, stagedAssets: [] }));
            navigateToStep('asset-method');
          }}
        />
      )}

      {/* Asset Wizard: Method picker (stage 2 — Manual / Bulk / Existing) */}
      {currentStep === 'asset-method' && formData.assetType && (
        <AssetMethodStep
          assetType={formData.assetType as AssetType}
          onChooseManual={() => {
            // Manual path: seed `stagedAssets[0]` and route to
            // `asset-details` for the user to fill in metadata.
            setFormData((prev) => ({
              ...prev,
              stagedAssets: [createEmptyStagedAsset(prev.assetType as AssetType)],
            }));
            navigateToStep('asset-details');
          }}
          onChooseBulk={() => navigateToStep('asset-bulk-upload')}
          onChooseExisting={() => {
            history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ASSETS, { rootAppPath, initiativeId }));
          }}
        />
      )}

      {/* Asset Wizard: Bulk XLSX upload (sub-step of asset-method) */}
      {currentStep === 'asset-bulk-upload' && formData.assetType && (
        <AssetBulkUploadStep
          initiativeId={initiativeId}
          assetType={formData.assetType as AssetType}
          onBulkParsed={(assets, file, bulkGroupId) => {
            // Parsed XLSX → N stagedAssets stamped with the shared
            // bulkGroupId. The source File is held in
            // `stagedBulkGroups` so submit can attach it as evidence
            // on the per-group `createBulkAssetImport` call. Skips
            // `asset-details` entirely (every asset's metadata's
            // already in the spreadsheet) and lands on
            // `asset-activities` for factor review.
            setFormData((prev) => ({ ...prev, stagedAssets: assets }));
            setStagedBulkGroups((prev) => [
              ...prev,
              { id: bulkGroupId, file, filename: file.name },
            ]);
            navigateToStep('asset-activities');
          }}
        />
      )}

      {/* Asset Wizard: Asset Details */}
      {currentStep === 'asset-details' && formData.stagedAssets[0] && (
        <AssetDetailsStep
          asset={formData.stagedAssets[0].asset}
          onUpdate={(patch) => setFormData((prev) => ({
            ...prev,
            stagedAssets: updateStagedAssetMeta(prev.stagedAssets, 0, patch),
          }))}
        />
      )}

      {/* Asset Wizard: Activities */}
      {currentStep === 'asset-activities' && (
        <AssetActivitiesStep
          stagedAssets={formData.stagedAssets}
          onStagedAssetsChange={(updater) => setFormData((prev) => ({ ...prev, stagedAssets: updater(prev.stagedAssets) }))}
          initiativeId={initiativeId}
          // Org-level default — used to pre-populate the Factor Region
          // on new activity rows when the asset itself has no region
          // set (manual path) or when the bulk row didn't ship a
          // region. Mirrors what the Activity flow's Entry step does.
          defaultRegion={settings?.defaultRegion ?? ''}
          // GU-6981: shared upload session so the Scan Invoice modal
          // per-asset-block stages files on the same session that the
          // post-submit Evidence step reads. Invoices end up attached
          // as evidence to the activities they produced.
          uploadSession={uploadSession}
        />
      )}

      {/* Evidence step:
        *   ByActivity / ByAsset — both run AFTER submit and consume
        *     `submittedImportIds` (one or more BulkImport ids). The
        *     wrapper component fetches the imported activities so the
        *     same routing-picker UX works for both flows.
        *   Single-activity edit / connect-method flows — `EvidenceStep`
        *     directly with the just-created activity id. */}
      {currentStep === 'evidence'
        && (dataMethod === DataMethod.ByActivity
            || dataMethod === DataMethod.ByAsset
            || dataMethod === DataMethod.ScanInvoice)
        && (
        <MultiAddEvidenceStep
          initiativeId={initiativeId ?? ''}
          importIds={submittedImportIds}
          uploadSession={uploadSession}
          onContinue={(attached) => {
            if (attached) {
              setAttachedEvidences(attached);
            }
            // `result` is a TRANSIENT_STEP — pushing it via navigateToStep
            // would trip the URL sync guard and bounce back to the method
            // picker. Update state directly; URL stays on `?step=evidence`.
            setCurrentStep('result');
          }}
        />
      )}
      {currentStep === 'evidence'
        && dataMethod !== DataMethod.ByAsset
        && dataMethod !== DataMethod.ByActivity
        && dataMethod !== DataMethod.ScanInvoice
        && (
        <EvidenceStep
          initiativeId={initiativeId ?? ''}
          activityIds={[submitResult?.data?._id ?? activityId].filter((id): id is string => Boolean(id))}
          uploadSession={uploadSession}
          onContinue={(attached) => {
            if (attached) {
              setAttachedEvidences(attached);
            }
            if (isEditing) {
              handleExitAfterSubmit().catch(handleRouteError);
              return;
            }
            setCurrentStep('result');
          }}
        />
      )}

      {/* Step Result — single-activity flow. `submitResult` only gets
        * populated by `handleSubmit` for create/update of one activity. */}
      {currentStep === 'result' && submitResult && (
        <ActivityResultStep
          result={submitResult}
          isEditing={isEditing}
          attachedEvidences={attachedEvidences}
          formatCompact={formatCompact}
          onViewActivities={handleExitAfterSubmit}
          onAddAnother={() => {
            uploadSession.commitSession().catch(handleRouteError);
            setFormData(INITIAL_FORM_DATA);
            setSubmitResult(null);
            setAttachedEvidences([]);
            setSubmitError(null);
            navigateToStep('method', null);
          }}
          onViewActivity={() => {
            uploadSession.commitSession().catch(handleRouteError);
            history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITY_DETAIL, {
              initiativeId,
              activityId: submitResult.data._id,
            }));
          }}
        />
      )}

      {/* Step Result — multi-add (ByActivity) flow. Activities were
        * created upstream in Entry step's submit; we aggregate over them
        * by `submittedImportIds`. */}
      {currentStep === 'result' && dataMethod === DataMethod.ByActivity && (
        <MultiAddResultStep
          initiativeId={initiativeId ?? ''}
          importIds={submittedImportIds}
          attachedEvidences={attachedEvidences}
          formatCompact={formatCompact}
          onViewActivities={handleExitAfterSubmit}
          onAddAnother={() => {
            uploadSession.commitSession().catch(handleRouteError);
            setFormData(INITIAL_FORM_DATA);
            setSubmittedImportIds([]);
            setAttachedEvidences([]);
            setSubmitError(null);
            navigateToStep('method', null);
          }}
          onViewActivity={(activityId) => {
            uploadSession.commitSession().catch(handleRouteError);
            history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITY_DETAIL, {
              initiativeId,
              activityId,
            }));
          }}
        />
      )}

      {/* Step Result — asset wizard (ByAsset) flow. Asset records were
        * created upstream by `handleAssetSubmit`; we aggregate by the
        * shared importIds for activity counts and CO₂e, and read
        * `submittedAssetIds` to drive the View Asset / View Assets CTA. */}
      {currentStep === 'result' && dataMethod === DataMethod.ByAsset && (
        <MultiAddAssetResultStep
          initiativeId={initiativeId ?? ''}
          importIds={submittedImportIds}
          assetIds={submittedAssetIds}
          attachedEvidences={attachedEvidences}
          formatCompact={formatCompact}
          onViewAssets={() => {
            uploadSession.commitSession().catch(handleRouteError);
            history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ASSETS, { rootAppPath, initiativeId }));
          }}
          onAddAnother={() => {
            uploadSession.commitSession().catch(handleRouteError);
            setFormData(INITIAL_FORM_DATA);
            setSubmittedImportIds([]);
            setSubmittedAssetIds([]);
            setAttachedEvidences([]);
            setSubmitError(null);
            navigateToStep('method', null);
          }}
          onViewAsset={(assetId) => {
            uploadSession.commitSession().catch(handleRouteError);
            history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ASSET_DASHBOARD, {
              initiativeId,
              assetId,
            }));
          }}
        />
      )}

      {/* Footer navigation. The wizard chrome owns Back for EVERY step
        * that has a "go back to the previous step" semantic. The only
        * exceptions are:
        *   - `method`  — terminal-top, no prior step.
        *   - `result`  — terminal-bottom; its own "Back to Review" CTA above.
        *   - `review` + ByActivity — uses "Cancel batch" with destructive
        *     undo semantics, not a plain Back. Stays in-card.
        *
        * Step-specific Submit / Continue CTAs (e.g. the "Submit N
        * activities" button on `entry`) live INSIDE the step's card.
        * Steps with their own in-page Continue CTAs that don't need a
        * chrome Next button are listed in BACK_ONLY_STEPS — those render
        * a chrome Back without a chrome Next.
        */}
      {currentStep !== 'method'
        && currentStep !== 'result'
        && !(currentStep === 'review'
              && (dataMethod === DataMethod.ByActivity || dataMethod === DataMethod.ScanInvoice))
        && (
        <div className='tw:flex tw:justify-between tw:mb-6'>
          <Button variant='outline' onClick={handleBack}>
            <ArrowLeft className='tw:size-4' />
            Back
          </Button>

          <div className='tw:flex tw:gap-2'>
            {(() => {
              const BACK_ONLY_STEPS: FormStep[] = [
                'entry',
                'stub-entry',
                'evidence',
                'asset-select',
                'asset-method',
                'asset-bulk-upload',
                'upload',
                'preview-import',
                'import-complete',
              ];
              if (BACK_ONLY_STEPS.includes(currentStep) || REQUEST_ACCESS_STEPS.includes(currentStep)) {
                return null;
              }
              // Asset wizard's submit lives on the asset-activities step
              // (no separate review step). Activity wizard's submit
              // lives on `review`.
              const isAssetSubmitStep = currentStep === 'asset-activities' && dataMethod === DataMethod.ByAsset;
              const isActivitySubmitStep =
                currentStep === 'review'
                && dataMethod !== DataMethod.ByAsset
                && dataMethod !== DataMethod.ByActivity
                && dataMethod !== DataMethod.ScanInvoice;
              if (isAssetSubmitStep || isActivitySubmitStep) {
                const totalStagedActivities = formData.stagedAssets.reduce(
                  (sum, a) => sum + a.activities.length, 0,
                );
                return (
                  <Button
                    onClick={isAssetSubmitStep ? handleAssetSubmit : handleSubmit}
                    disabled={
                      isCreating || isUpdating || isCreatingAsset
                      || (isAssetSubmitStep && totalStagedActivities === 0)
                    }
                    className='tw:bg-emerald-800 tw:hover:bg-emerald-900 tw:text-white tw:border-0'
                  >
                    {isCreating || isUpdating || isCreatingAsset ? (
                      <>
                        <Loader2 className='tw:size-4 tw:animate-spin' />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className='tw:size-4' />
                        {isAssetSubmitStep
                          ? `Create ${formData.stagedAssets.length} Asset${formData.stagedAssets.length === 1 ? '' : 's'} & ${totalStagedActivities} Activit${totalStagedActivities === 1 ? 'y' : 'ies'}`
                          : isEditing ? 'Update Activity' : 'Create Activity'}
                      </>
                    )}
                  </Button>
                );
              }
              return (
                <Button
                  onClick={handleNext}
                  disabled={
                    (currentStep === 'activity' && formData.activityType === '' && formData.scope === '') ||
                    (currentStep === 'details' && !canProceedFromDetails) ||
                    (currentStep === 'asset-details' && (
                      !formData.stagedAssets[0]?.asset.name.trim()
                      || !formData.stagedAssets[0]?.asset.assetType
                    ))
                  }
                  className='tw:bg-emerald-800 tw:hover:bg-emerald-900 tw:text-white tw:border-0'
                >
                  Next
                  <ArrowRight className='tw:size-4' />
                </Button>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
};
